import type { Blockquote, BlockContent, Code, DefinitionContent, Heading, List, Node, Parent, PhrasingContent, Root, RootContent, Text, ThematicBreak } from 'mdast';
import { toString } from 'mdast-util-to-string';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';

/**
 * 대화 본문(표준 마크다운 + GFM 취소선)을 표시 블록으로 나눈다. 서식 규약 정본 docs/narrative-format.md (AC0-254) — 캐릭터 응답·내 메시지·플롯 text/dialogue 블록 공용.
 * - `> ` 인용 문단 → scene(장면, 인용 안 문단마다 한 블록).
 * - 문단의 최상위 `em`(`*…*`) 구간 → narration(지문, 마커 제거), 나머지 → dialogue(말풍선). `**…**` 는 굵게일 뿐 지문이 아니다(문단 전체가 굵게여도 dialogue).
 *   말풍선 구간이 통째로 따옴표면 뗀다(옛 기록 호환).
 * - 문단 전체가 작은따옴표 한 쌍(`'…'`·`‘…’`)이면 thought(속마음, 따옴표 제거, AC0-244). 문단 안에 섞인 작은따옴표(인용·강조)는 그대로.
 * - 제목·코드·목록·구분선은 block. HTML·링크·이미지·표 등 그 밖의 것은 원문 글자 그대로 dialogue.
 * - streaming 이면 닫히지 않은 코드 펜스·`` ` ``·`**`·`*` 를 임시로 닫고 파싱한다(깜빡임 방지). 마지막 문단이 작은따옴표로 열리고 아직 닫히지 않았으면 thought 로 본다. 열린 `> ` 줄은 그대로 scene.
 */
export type NarrativeBlock =
  | { kind: 'scene'; children: PhrasingContent[] }
  | { kind: 'narration'; children: PhrasingContent[] }
  | { kind: 'dialogue'; children: PhrasingContent[] }
  | { kind: 'thought'; children: PhrasingContent[] }
  | { kind: 'block'; node: Heading | Code | List | ThematicBreak };

const processor = unified().use(remarkParse).use(remarkGfm, { singleTilde: false }); // `안녕~반가워~` 는 취소선이 아니다

const OPEN_QUOTE = /^["“]/;
const OPEN_SINGLE = /^['‘]/;
const CLOSE_SINGLE = /['’]$/;
const CLOSE_QUOTE = /["”]$/;
const FENCE = /^ {0,3}(`{3,}|~{3,})/;

/** 스트리밍 중 열린 마커를 끝에 닫아 준다. 코드 펜스 안의 글자는 세지 않는다 */
const HR_LINE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const BULLET = /^\s*(?:[-*+]|\d+[.)])\s+/;

/**
 * 스트리밍 중 열린 마커를 닫아 준다. 코드 펜스는 본문 전체, 인라인 마커(`` ` ``·`**`·`*`)는 마지막 문단만 센다(강조는 문단을 넘지 못한다).
 * 구분선 줄·목록 글머리는 세지 않고, 닫는 마커는 끝 공백 앞에 넣는다. 마커 뒤에 글자가 없으면(`…*`·`**`) 닫지 않고 그 마커를 뗀다.
 */
const closeOpenMarkers = (text: string): string => {
  let fence: string | null = null;
  for (const line of text.split('\n')) {
    const m = FENCE.exec(line);
    // 백틱 펜스의 정보 문자열에는 백틱이 올 수 없다(CommonMark) — "```상태```" 는 인라인 코드
    if (m && m[1]![0] === '`' && line.slice(m[0].length).includes('`')) continue;
    if (fence) {
      if (m && m[1]![0] === fence[0] && m[1]!.length >= fence.length) fence = null;
    } else if (m) {
      fence = m[1]!;
    }
  }
  if (fence) return `${text}\n${fence}`;

  const paraStart = text.search(/(?:^|\n[ \t]*\n)(?![\s\S]*\n[ \t]*\n)/);
  const head = text.slice(0, paraStart === -1 ? 0 : paraStart);
  const para = text.slice(head.length);
  const trailing = /\s*$/.exec(para)![0];
  const body = para.slice(0, para.length - trailing.length);
  const counted = body
    .split('\n')
    .filter((l) => !HR_LINE.test(l))
    .map((l) => l.replace(BULLET, ''))
    .join('\n');
  let tail = '';
  if ((counted.match(/`/g) ?? []).length % 2 === 1) tail += '`';
  const doubles = (counted.match(/\*\*/g) ?? []).length;
  if (doubles % 2 === 1) tail += '**';
  if ((counted.match(/\*/g) ?? []).length - doubles * 2 === 1) tail += '*';
  if (!tail) return text;
  const dangling = /[*`]+$/.exec(body);
  if (dangling) return closeOpenMarkers(`${head}${body.slice(0, dangling.index)}${trailing}`); // 뗀 뒤 그 앞의 열린 마커를 다시 본다
  return `${head}${body}${tail}${trailing}`;
};

/** 원문 조각을 글자 그대로 담은 text 노드 */
const raw = (node: Node, source: string): Text => {
  const value = node.position ? source.slice(node.position.start.offset, node.position.end.offset) : toString(node);
  return node.position ? { type: 'text', value, position: node.position } : { type: 'text', value };
};

const INLINE_KEEP = new Set(['text', 'emphasis', 'strong', 'delete', 'inlineCode', 'break']);

/** 렌더하지 않는 인라인(HTML·링크·이미지 등)을 원문 text 로 바꾼다 */
const sanitizeInline = (nodes: PhrasingContent[], source: string): PhrasingContent[] =>
  nodes.map((n) => {
    if (!INLINE_KEEP.has(n.type)) return raw(n, source);
    if ('children' in n) return { ...n, children: sanitizeInline(n.children as PhrasingContent[], source) } as PhrasingContent;
    return n;
  });

const NESTED_KEEP = new Set(['paragraph', 'heading', 'code', 'list', 'listItem', 'blockquote', 'thematicBreak']);

/** 블록 트리 정리: 문단·제목의 인라인은 sanitizeInline, 인용·목록 안의 그릴 수 없는 블록(HTML·표 등)은 원문 문단으로 */
const sanitizeBlock = <T extends Node>(node: T, source: string): T => {
  if (!('children' in node)) return node;
  const parent = node as unknown as Parent;
  const children = parent.children.map((c) => {
    if (c.type === 'paragraph' || c.type === 'heading') return { ...c, children: sanitizeInline(c.children, source) };
    if (node.type !== 'root' && !NESTED_KEEP.has(c.type)) return { type: 'paragraph', children: [raw(c, source)] };
    return sanitizeBlock(c, source);
  });
  return { ...node, children } as T;
};

const isBlank = (n: PhrasingContent): boolean => n.type === 'text' && n.value.trim() === '';

const trimEdges = (run: PhrasingContent[]): PhrasingContent[] => {
  const out = [...run];
  while (out.length && isBlank(out[0]!)) out.shift();
  while (out.length && isBlank(out[out.length - 1]!)) out.pop();
  if (!out.length) return out;
  const first = out[0]!;
  if (first.type === 'text') out[0] = { ...first, value: first.value.replace(/^\s+/, '') };
  const last = out[out.length - 1]!;
  if (last.type === 'text') out[out.length - 1] = { ...last, value: last.value.replace(/\s+$/, '') };
  return out;
};

/** 말풍선 구간이 통째로 한 쌍의 따옴표로 감싸여 있으면 뗀다 (D14) */
const stripQuotes = (run: PhrasingContent[]): PhrasingContent[] => {
  const first = run[0];
  const last = run[run.length - 1];
  if (!first || !last || first.type !== 'text' || last.type !== 'text') return run;
  if (!OPEN_QUOTE.test(first.value) || !CLOSE_QUOTE.test(last.value)) return run;
  if (first === last && first.value.length < 2) return run;
  // 안에 다른 따옴표가 있으면(`"안녕." "잘 지냈어?"`) 한 쌍이 아니므로 두지 않는다
  if (/["“”]/.test(toString(run).slice(1, -1))) return run;
  const out = [...run];
  out[0] = { ...first, value: first.value.replace(OPEN_QUOTE, '') };
  const l = out[out.length - 1] as Text;
  out[out.length - 1] = { ...l, value: l.value.replace(CLOSE_QUOTE, '') };
  return trimEdges(out);
};

/**
 * 문단 전체가 작은따옴표 한 쌍이면 따옴표를 뗀 속마음 내용, 아니면 null (AC0-244).
 * 안에 다른 작은따옴표가 있으면(`'뭐'가 나야`·`'a' 하고 'b'`) 한 쌍이 아니다. 영문 축약(don't)의 아포스트로피는 세지 않는다.
 * open: 스트리밍 중 마지막 문단 — 닫는 따옴표가 아직 안 왔어도 속마음으로 본다.
 */
const thoughtChildren = (children: PhrasingContent[], open: boolean): PhrasingContent[] | null => {
  const run = trimEdges(children);
  const first = run[0];
  const last = run[run.length - 1];
  if (!first || !last || first.type !== 'text' || !OPEN_SINGLE.test(first.value)) return null;
  const closed = last.type === 'text' && CLOSE_SINGLE.test(last.value) && !(first === last && first.value.length < 2);
  if (!closed && !open) return null;
  const all = toString(run);
  const inner = (closed ? all.slice(1, -1) : all.slice(1)).replace(/[A-Za-z]['’][A-Za-z]/g, '');
  if (/['‘’]/.test(inner)) return null;
  const out = [...run];
  out[0] = { ...first, value: first.value.replace(OPEN_SINGLE, '') };
  if (closed) {
    const l = out[out.length - 1] as Text;
    out[out.length - 1] = { ...l, value: l.value.replace(CLOSE_SINGLE, '') };
  }
  const trimmed = trimEdges(out);
  return trimmed.length ? trimmed : open ? [] : null;
};

/** 문단 → 지문(최상위 em 구간)·말풍선(나머지) 블록 */
const paragraphBlocks = (children: PhrasingContent[]): NarrativeBlock[] => {
  const out: NarrativeBlock[] = [];
  let run: PhrasingContent[] = [];
  let kind: 'narration' | 'dialogue' | null = null;
  const flush = () => {
    if (!kind) return;
    let trimmed = trimEdges(run);
    if (kind === 'dialogue') trimmed = stripQuotes(trimmed);
    if (trimmed.length) out.push({ kind, children: trimmed });
    run = [];
    kind = null;
  };
  for (const node of children) {
    if (isBlank(node)) {
      if (kind) run.push(node);
      continue;
    }
    const next = node.type === 'emphasis' ? 'narration' : 'dialogue';
    if (kind && kind !== next) flush();
    kind = next;
    if (node.type === 'emphasis') run.push(...node.children);
    else run.push(node);
  }
  flush();
  return out;
};

/** 인용(`> `) → 장면. 안의 문단·제목은 장면 한 블록씩, 겹인용은 펼치고, 코드·목록·구분선은 그 블록 그대로 */
const sceneBlocks = (children: (BlockContent | DefinitionContent)[]): NarrativeBlock[] =>
  children.flatMap((c): NarrativeBlock[] => {
    switch (c.type) {
      case 'paragraph':
      case 'heading': {
        const run = trimEdges(c.children);
        return run.length ? [{ kind: 'scene', children: run }] : [];
      }
      case 'blockquote':
        return sceneBlocks((c as Blockquote).children);
      case 'code':
      case 'list':
      case 'thematicBreak':
        return [{ kind: 'block', node: c }];
      default:
        return [];
    }
  });

const toBlocks = (node: RootContent, source: string, openThought: boolean): NarrativeBlock[] => {
  switch (node.type) {
    case 'paragraph': {
      const thought = thoughtChildren(node.children, openThought);
      if (thought) return [{ kind: 'thought', children: thought }];
      return paragraphBlocks(node.children);
    }
    case 'blockquote':
      return sceneBlocks(node.children);
    case 'heading':
    case 'code':
    case 'list':
    case 'thematicBreak':
      return [{ kind: 'block', node }];
    default: {
      const text = raw(node, source);
      return text.value.trim() ? [{ kind: 'dialogue', children: [text] }] : [];
    }
  }
};

export function parseNarrative(text: string, opts?: { streaming?: boolean }): NarrativeBlock[] {
  const source = opts?.streaming ? closeOpenMarkers(text) : text;
  const root = sanitizeBlock(processor.parse(source) as Root, source);
  const last = root.children.length - 1;
  return root.children.flatMap((n, i) => toBlocks(n, source, !!opts?.streaming && i === last));
}

/** 블록 하나의 일반 글(마커 없음). 블록 안 줄바꿈은 유지 */
export function blockText(block: NarrativeBlock): string {
  if (block.kind !== 'block') return toString(block.children);
  return nestedText(block.node);
}

/** 목록(안의 인용 포함) 블록은 줄바꿈으로 잇고, 목록 항목은 글머리(•·번호)를 붙인다 */
const nestedText = (node: Node): string => {
  switch (node.type) {
    case 'code':
      return (node as Code).value;
    case 'list': {
      const list = node as List;
      const start = list.start ?? 1;
      return list.children.map((item, i) => `${list.ordered ? `${start + i}.` : '•'} ${nestedText(item)}`).join('\n');
    }
    case 'listItem':
    case 'blockquote':
      return (node as Parent).children.map(nestedText).filter(Boolean).join('\n');
    default:
      return toString(node);
  }
};

/** 본문 전체의 일반 글. 블록을 줄바꿈으로 잇는다. 공백뿐이면 '' */
export function narrativeText(text: string): string {
  return parseNarrative(text)
    .map(blockText)
    .filter((s) => s.length > 0)
    .join('\n');
}

/**
 * 타입 블록 → 서식 규약 마크다운(docs/narrative-format.md). scene·thought 블록은 타입이 종류를 정하고 본문은 줄 안 서식만 쓴다.
 * 작성자가 기호까지 붙여 쓴 경우(`> 장면`, `'속마음'`)도 같은 결과가 되도록 겹친 기호는 뗀다(AC0-254).
 */
const stripScenePrefix = (line: string) => line.replace(/^\s*>\s?/, '').trim();
const stripThoughtQuotes = (body: string) => body.trim().replace(/^['‘]([\s\S]*)['’]$/, '$1').trim();

/** scene 블록 본문 → `> …` 장면 문단(줄마다 `>`) */
export const sceneMarkdown = (body: string): string =>
  body
    .split('\n')
    .map((l) => stripScenePrefix(l))
    .map((l) => (l ? `> ${l}` : '>'))
    .join('\n');

/** thought 블록 본문 → `'…'` 속마음 한 문단(줄바꿈은 공백으로) */
export const thoughtMarkdown = (body: string): string => `'${stripThoughtQuotes(body).replace(/\s*\n\s*/g, ' ')}'`;
